import { X, Printer } from 'lucide-react';
import type { Event, PlanningItem } from '@peles/shared';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';

export interface ItemizedPrintFilter {
  label: string;
  values: string[];
}

interface Props {
  event: Event & {
    eventTypeName?: string;
    venueName?: string;
    logoUrl?: string | null;
  };
  /** Rows to print — already filtered by the caller. */
  items: PlanningItem[];
  /** Active filters shown in the report header (only non-empty ones). */
  activeFilters: ItemizedPrintFilter[];
  onClose: () => void;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(d: Date | string | null | undefined) {
  if (!d) return '—';
  const dt = new Date(d);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()).toLocaleDateString(
    'en-US',
    { month: 'long', day: 'numeric', year: 'numeric' },
  );
}

function esc(s: string | null | undefined) {
  if (!s) return '';
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function itemLabel(item: PlanningItem): string {
  return item.itemName ?? item.catalogItemName ?? '—';
}

export function ItemizedPrintModal({ event, items, activeFilters, onClose }: Props) {
  const { data: appLogoUrl } = useAppSettingValue('app_logo_s3_key');

  const logoSrc =
    event.logoUrl ?? (typeof appLogoUrl === 'string' ? appLogoUrl : null) ?? '/logo.png';

  const filters = activeFilters.filter((f) => f.values.length > 0);
  const grandTotal = items.reduce((sum, i) => sum + i.total, 0);

  const eventDate = event.startAt
    ? (() => {
        const start = formatDate(event.startAt);
        if (event.endAt) {
          const end = formatDate(event.endAt);
          return start === end ? start : `${start} – ${end}`;
        }
        return start;
      })()
    : null;

  function handlePrint() {
    const win = window.open('', '_blank', 'width=900,height=800');
    if (!win) return;

    const thStyle =
      'padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;';
    const tdStyle = 'padding:8px 10px;border-bottom:1px solid #f3f4f6;';

    const rowsHtml =
      items.length === 0
        ? `<tr><td colspan="8" style="padding:12px 10px;color:#9ca3af;font-style:italic;">No items.</td></tr>`
        : items
            .map(
              (item) => `
          <tr>
            <td style="${tdStyle}color:#6b7280;">${esc(item.vendorCategoryName ?? '—')}</td>
            <td style="${tdStyle}font-weight:500;">${esc(itemLabel(item))}</td>
            <td style="${tdStyle}color:#6b7280;">${esc(item.roomName ?? '—')}</td>
            <td style="${tdStyle}text-align:right;">${item.quantity}</td>
            <td style="${tdStyle}text-align:right;">${esc(fmt(item.unitPrice))}</td>
            <td style="${tdStyle}text-align:right;font-weight:500;">${esc(fmt(item.total))}</td>
            <td style="${tdStyle}color:#6b7280;">${esc(item.expenseItemName ?? '—')}</td>
            <td style="${tdStyle}color:#6b7280;">${esc(item.expenseVendorName ?? '—')}</td>
          </tr>`,
            )
            .join('');

    const eventMeta = [eventDate, event.venueName, event.eventTypeName]
      .filter(Boolean)
      .map((line) => `<div>${esc(String(line))}</div>`)
      .join('');

    const filtersHtml =
      filters.length === 0
        ? ''
        : `<div style="margin-bottom:20px;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;font-size:12px;color:#374151;">
            <span style="font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:0.05em;color:#6b7280;margin-right:8px;">Filtered by</span>
            ${filters
              .map(
                (f) =>
                  `<span style="margin-right:14px;"><strong>${esc(f.label)}:</strong> ${esc(f.values.join(', '))}</span>`,
              )
              .join('')}
          </div>`;

    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Itemized List — ${esc(event.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; padding: 36px; color: #111827; font-size: 13px; line-height: 1.5; }
    @media print {
      body { padding: 20px; }
      @page { margin: 1.5cm; size: A4; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div style="display:flex;align-items:flex-start;gap:20px;padding-bottom:20px;border-bottom:1px solid #e5e7eb;margin-bottom:20px;">
    <img src="${logoSrc}" alt="Logo"
      style="width:64px;height:64px;object-fit:contain;border:1px solid #e5e7eb;border-radius:8px;flex-shrink:0;background:#fff;"
      onerror="this.src='/logo.png'"/>
    <div>
      <div style="font-size:20px;font-weight:700;color:#111827;margin-bottom:4px;">${esc(event.name)}</div>
      <div style="font-size:13px;color:#6b7280;line-height:1.7;">${eventMeta}</div>
    </div>
    <div style="margin-left:auto;text-align:right;">
      <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#6b7280;">Itemized List</div>
      <div style="font-size:12px;color:#9ca3af;">${items.length} item${items.length !== 1 ? 's' : ''}</div>
    </div>
  </div>

  ${filtersHtml}

  <table style="width:100%;border-collapse:collapse;font-size:13px;">
    <thead>
      <tr>
        <th style="${thStyle}text-align:left;">Category</th>
        <th style="${thStyle}text-align:left;">Item</th>
        <th style="${thStyle}text-align:left;">Room</th>
        <th style="${thStyle}text-align:right;">Qty</th>
        <th style="${thStyle}text-align:right;">Unit Price</th>
        <th style="${thStyle}text-align:right;">Total</th>
        <th style="${thStyle}text-align:left;">Expense</th>
        <th style="${thStyle}text-align:left;">Vendor</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
      ${
        items.length > 0
          ? `<tr style="background:#f9fafb;font-weight:600;">
        <td colspan="5" style="padding:9px 10px;border-top:2px solid #e5e7eb;">Total</td>
        <td style="padding:9px 10px;border-top:2px solid #e5e7eb;text-align:right;">${esc(fmt(grandTotal))}</td>
        <td colspan="2" style="padding:9px 10px;border-top:2px solid #e5e7eb;"></td>
      </tr>`
          : ''
      }
    </tbody>
  </table>

</body>
</html>`);

    win.document.close();
    win.focus();
    // Small delay lets the logo image load before print dialog opens
    setTimeout(() => win.print(), 600);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Itemized List</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-md text-sm hover:bg-gray-900"
              data-testid="itemized-print-button"
            >
              <Printer size={14} />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable preview */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* Header */}
          <div className="flex items-start gap-4 pb-5 border-b">
            <img
              src={logoSrc}
              alt="Logo"
              style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }}
              className="rounded-lg border border-gray-200 bg-white"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/logo.png';
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{event.name}</h1>
              <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                {eventDate && <p>{eventDate}</p>}
                {event.venueName && <p>{event.venueName}</p>}
                {event.eventTypeName && <p>{event.eventTypeName}</p>}
              </div>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Itemized List
              </p>
              <p className="text-xs text-gray-400">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Active filters */}
          {filters.length > 0 && (
            <div
              className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-xs text-gray-700 flex flex-wrap gap-x-4 gap-y-1"
              data-testid="itemized-print-filters"
            >
              <span className="font-semibold uppercase text-[10px] tracking-wide text-gray-500">
                Filtered by
              </span>
              {filters.map((f) => (
                <span key={f.label}>
                  <strong>{f.label}:</strong> {f.values.join(', ')}
                </span>
              ))}
            </div>
          )}

          {/* Items table */}
          {items.length === 0 ? (
            <p className="text-sm text-gray-400">No items.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                    Category
                  </th>
                  <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                    Item
                  </th>
                  <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                    Room
                  </th>
                  <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                    Qty
                  </th>
                  <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                    Unit Price
                  </th>
                  <th className="text-right py-2 text-xs text-gray-500 font-semibold uppercase">
                    Total
                  </th>
                  <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                    Expense
                  </th>
                  <th className="text-left py-2 text-xs text-gray-500 font-semibold uppercase">
                    Vendor
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-2 text-gray-500">{item.vendorCategoryName ?? '—'}</td>
                    <td className="py-2 font-medium">{itemLabel(item)}</td>
                    <td className="py-2 text-gray-500">{item.roomName ?? '—'}</td>
                    <td className="py-2 text-right">{item.quantity}</td>
                    <td className="py-2 text-right">{fmt(item.unitPrice)}</td>
                    <td className="py-2 text-right font-medium">{fmt(item.total)}</td>
                    <td className="py-2 text-gray-500">{item.expenseItemName ?? '—'}</td>
                    <td className="py-2 text-gray-500">{item.expenseVendorName ?? '—'}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-gray-50">
                  <td className="py-2" colSpan={5}>
                    Total
                  </td>
                  <td className="py-2 text-right">{fmt(grandTotal)}</td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
